import { Flex, List, MoreHorizontalIcon, Popover } from '@iheartjane/reefer';
import type { PopoverContextProps } from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { Fragment } from 'react';

import styles from './cardMenu.module.css';

interface ProductCardMenuItemRenderArgs {
  closeMenu: () => void;
}

export interface ProductCardMenuItem {
  /** Stable identifier */
  key: string;
  /** Renders a single `List.Item` */
  render: (args: ProductCardMenuItemRenderArgs) => ReactNode;
}

interface CardMenuProps {
  items: ProductCardMenuItem[];
  productName: string;
}

/**
 * Overflow menu for a product card. Owns the button, popover and list;
 * callers supply the rows. Knows nothing about what any row does.
 */
export const CardMenu = ({ items, productName }: CardMenuProps) => {
  if (items.length === 0) return null;

  const label = `${productName} options`;

  return (
    <Popover
      alignment={{ horizontal: 'right', vertical: 'bottom' }}
      className={styles['menu']}
      contentRole="dialog"
      disableMobileStyling
      label={label}
      noMinWidth
      target={
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles['trigger']}
          borderRadius="xl"
          height="var(--card-menu-size, 28px)"
          width="var(--card-menu-size, 28px)"
          p={4}
        >
          <MoreHorizontalIcon rotate="right" size="md" />
        </Flex>
      }
    >
      {({ closePopover }: PopoverContextProps) => (
        <Popover.Content label={label} padding={false}>
          <List label={label}>
            {items.map(({ key, render }) => (
              <Fragment key={key}>
                {render({ closeMenu: closePopover })}
              </Fragment>
            ))}
          </List>
        </Popover.Content>
      )}
    </Popover>
  );
};
